import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { useMergedRefs } from 'owa-react-hooks/lib/useMergedRefs';
import type { AssetId } from 'owa-timestream-types';
import {
    useResizeableElementState,
    HorizontalEdgeType,
    VerticalEdgeType,
} from '../hooks/useResizeableElementState';
import classNames from 'classnames';

import * as styles from './ResizeableElement.scss';

export default observer(
    function ResizeableElement(
        {
            assetId,
            children,
            attributes,
        }: {
            assetId: AssetId;
            children: React.ReactNode;
            attributes?: React.HTMLAttributes<HTMLDivElement>;
        },
        ref: React.Ref<HTMLDivElement>
    ) {
        const resizeRef = React.useRef<HTMLDivElement>(null);
        const mergedRef = useMergedRefs(ref, resizeRef);
        const {
            positioningAttributes,
            isSelected,
            isResizeHandlesShown,
            createPointerEvents,
        } = useResizeableElementState(resizeRef, assetId);

        const rootDivAttributes = {
            ...attributes,
            style: {
                ...attributes?.style,
                ...positioningAttributes?.style,
            },
        };

        if (!createPointerEvents) {
            return (
                <div
                    ref={mergedRef}
                    className={classNames(styles.root, rootDivAttributes.className || '')}
                    {...rootDivAttributes}>
                    {children}
                </div>
            );
        }

        const visibleClassName = isResizeHandlesShown && isSelected ? '' : styles.invisible;
        return (
            <div
                ref={mergedRef}
                {...rootDivAttributes}
                className={classNames(
                    styles.root,
                    styles.resizeEnabled,
                    rootDivAttributes.className || '',
                    {
                        [styles.isSelected]: isSelected,
                    }
                )}>
                {children}
                {/** The circles for cardinal directions. Visible if asset selected */}
                <div
                    {...createPointerEvents(HorizontalEdgeType.Top)}
                    className={classNames(
                        visibleClassName,
                        styles.resizeCircle,
                        styles.topCircle,
                        styles.topCursor
                    )}
                />
                <div
                    {...createPointerEvents(undefined, VerticalEdgeType.Left)}
                    className={classNames(
                        visibleClassName,
                        styles.resizeCircle,
                        styles.leftCircle,
                        styles.leftCursor
                    )}
                />
                <div
                    {...createPointerEvents(undefined, VerticalEdgeType.Right)}
                    className={classNames(
                        visibleClassName,
                        styles.resizeCircle,
                        styles.rightCircle,
                        styles.rightCursor
                    )}
                />
                <div
                    {...createPointerEvents(HorizontalEdgeType.Bottom)}
                    className={classNames(
                        visibleClassName,
                        styles.resizeCircle,
                        styles.bottomCircle,
                        styles.bottomCursor
                    )}
                />
                {/** The lines for cardinal directions, along all edges of asset. Always invisible but always possible to drag */}
                <div
                    {...createPointerEvents(HorizontalEdgeType.Top)}
                    className={classNames(
                        styles.invisible,
                        styles.resizeLine,
                        styles.topLine,
                        styles.topCursor
                    )}
                />
                <div
                    {...createPointerEvents(undefined, VerticalEdgeType.Left)}
                    className={classNames(
                        styles.invisible,
                        styles.resizeLine,
                        styles.leftLine,
                        styles.leftCursor
                    )}
                />
                <div
                    {...createPointerEvents(undefined, VerticalEdgeType.Right)}
                    className={classNames(
                        styles.invisible,
                        styles.resizeLine,
                        styles.rightLine,
                        styles.rightCursor
                    )}
                />
                <div
                    {...createPointerEvents(HorizontalEdgeType.Bottom)}
                    className={classNames(
                        styles.invisible,
                        styles.resizeLine,
                        styles.bottomLine,
                        styles.bottomCursor
                    )}
                />
                {/** The circles for corners for bi-directional. Visible if asset selected. End of list to render on top of the lines for priority */}
                <div
                    {...createPointerEvents(HorizontalEdgeType.Top, VerticalEdgeType.Left)}
                    className={classNames(visibleClassName, styles.resizeCircle, styles.topLeft)}
                />
                <div
                    {...createPointerEvents(HorizontalEdgeType.Top, VerticalEdgeType.Right)}
                    className={classNames(visibleClassName, styles.resizeCircle, styles.topRight)}
                />
                <div
                    {...createPointerEvents(HorizontalEdgeType.Bottom, VerticalEdgeType.Left)}
                    className={classNames(visibleClassName, styles.resizeCircle, styles.bottomLeft)}
                />
                <div
                    {...createPointerEvents(HorizontalEdgeType.Bottom, VerticalEdgeType.Right)}
                    className={classNames(
                        visibleClassName,
                        styles.resizeCircle,
                        styles.bottomRight
                    )}
                />
            </div>
        );
    },
    { forwardRef: true }
);
